import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Globe, LogOut, Menu, X, Database, ShieldCheck } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export function Logo({ className = "" }) {
  return (
    <Link to="/" data-testid="logo-link" className={`flex items-center gap-2 ${className}`}>
      <span className="inline-flex h-8 w-8 items-center justify-center bg-[#002FA7] text-white">
        <Database className="h-4 w-4" strokeWidth={2.5} />
      </span>
      <span className="font-display text-xl font-black tracking-tight">SAVEBOX</span>
    </Link>
  );
}

function LangSwitcher() {
  const { lang, setLang } = useI18n();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="rounded-sm gap-2 font-mono text-xs uppercase"
          data-testid="lang-switcher-button"
        >
          <Globe className="h-3.5 w-3.5" />
          {lang.toUpperCase()}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-sm font-mono">
        <DropdownMenuItem onClick={() => setLang("fr")} data-testid="lang-fr">
          FR — Français
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setLang("en")} data-testid="lang-en">
          EN — English
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Header({ variant = "marketing" }) {
  const { t } = useI18n();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isApp = variant === "app";

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-8">
          <Logo />
          {!isApp ? (
            <nav className="hidden items-center gap-6 md:flex">
              <a href="/#features" className="text-sm text-muted-foreground hover:text-foreground transition" data-testid="nav-features">
                {t("nav.features")}
              </a>
              <a href="/#how" className="text-sm text-muted-foreground hover:text-foreground transition" data-testid="nav-how">
                {t("nav.how")}
              </a>
              <Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground transition" data-testid="nav-pricing">
                {t("nav.pricing")}
              </Link>
              <Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition" data-testid="nav-contact">
                Contact
              </Link>
              <a href="/#faq" className="text-sm text-muted-foreground hover:text-foreground transition" data-testid="nav-faq">
                {t("nav.faq")}
              </a>
            </nav>
          ) : (
            <nav className="hidden items-center gap-1 md:flex">
              <NavLink
                to="/dashboard"
                end
                className={({ isActive }) =>
                  `rounded-sm px-3 py-1.5 text-sm transition ${isActive ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`
                }
                data-testid="nav-dashboard"
              >
                {t("nav.dashboard")}
              </NavLink>
              <NavLink
                to="/accounts"
                className={({ isActive }) =>
                  `rounded-sm px-3 py-1.5 text-sm transition ${isActive ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`
                }
                data-testid="nav-accounts"
              >
                {t("nav.accounts")}
              </NavLink>
              <NavLink
                to="/settings"
                className={({ isActive }) =>
                  `rounded-sm px-3 py-1.5 text-sm transition ${isActive ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`
                }
                data-testid="nav-settings"
              >
                {t("nav.settings")}
              </NavLink>
            </nav>
          )}
        </div>

        <div className="flex items-center gap-2">
          <LangSwitcher />
          {!user ? (
            <Button
              size="sm"
              className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
              onClick={() => navigate("/login")}
              data-testid="header-login-button"
            >
              {t("nav.login")}
            </Button>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 rounded-sm border border-border bg-card px-2 py-1 hover:bg-accent transition"
                  data-testid="user-menu-button"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="h-6 w-6 rounded-sm" />
                  ) : (
                    <span className="flex h-6 w-6 items-center justify-center bg-[#002FA7] text-white text-xs font-mono">
                      {user.email[0].toUpperCase()}
                    </span>
                  )}
                  <span className="hidden text-xs font-mono sm:inline">{user.email}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-sm">
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                  {t("nav.dashboard")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/accounts")} data-testid="menu-accounts">
                  {t("nav.accounts")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                  {t("nav.settings")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/pricing")} data-testid="menu-billing">
                  {t("nav.billing")}
                </DropdownMenuItem>
                {user.is_admin && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <ShieldCheck className="mr-2 h-4 w-4" />
                    {t("nav.admin")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut} data-testid="menu-logout">
                  <LogOut className="mr-2 h-4 w-4" />
                  {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <button
            className="ml-1 md:hidden"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-border/60 bg-background md:hidden">
          <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-4">
            {!isApp ? (
              <>
                <a href="/#features" className="py-2 text-sm" onClick={() => setMobileOpen(false)} data-testid="mnav-features">{t("nav.features")}</a>
                <a href="/#how" className="py-2 text-sm" onClick={() => setMobileOpen(false)} data-testid="mnav-how">{t("nav.how")}</a>
                <Link to="/pricing" className="py-2 text-sm" onClick={() => setMobileOpen(false)} data-testid="mnav-pricing">{t("nav.pricing")}</Link>
                <Link to="/contact" className="py-2 text-sm" onClick={() => setMobileOpen(false)} data-testid="mnav-contact">Contact</Link>
                <a href="/#faq" className="py-2 text-sm" onClick={() => setMobileOpen(false)} data-testid="mnav-faq">{t("nav.faq")}</a>
              </>
            ) : (
              <>
                <NavLink to="/dashboard" className="py-2 text-sm" onClick={() => setMobileOpen(false)} data-testid="mnav-dashboard">{t("nav.dashboard")}</NavLink>
                <NavLink to="/accounts" className="py-2 text-sm" onClick={() => setMobileOpen(false)} data-testid="mnav-accounts">{t("nav.accounts")}</NavLink>
                <NavLink to="/settings" className="py-2 text-sm" onClick={() => setMobileOpen(false)} data-testid="mnav-settings">{t("nav.settings")}</NavLink>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
